import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaUpload,
  FaTrash,
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaBrain,
} from "react-icons/fa";
import { useLocation } from "react-router-dom";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpaSingleKeyword.css";

const SpaSingleKeyword = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  const location = useLocation();

  // Detect if we're in grouping mode based on URL
  const isGroupingMode = location.pathname.includes("keyword-group");

  // Estados para archivos
  const [keywordFile, setKeywordFile] = useState(null);
  const [asinFile, setAsinFile] = useState(null);
  const [negativeFile, setNegativeFile] = useState(null);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creationDiagnostics, setCreationDiagnostics] = useState(null);
  const [creationComplete, setCreationComplete] = useState(false); // Track if campaigns were created in Amazon
  const [campaignState, setCampaignState] = useState("PAUSED"); // Default state for campaigns: PAUSED for safety
  const [step, setStep] = useState(1);

  // AI Grouping states
  const [groupingQuery, setGroupingQuery] = useState("");
  const [groupingAnalysis, setGroupingAnalysis] = useState(null);

  // Bulk actions states
  const [bulkAction, setBulkAction] = useState(""); // Selected bulk action
  const [bulkBudget, setBulkBudget] = useState(""); // Budget for bulk assignment
  const [bulkStartDate, setBulkStartDate] = useState(""); // Start date for bulk assignment
  const [bulkEndDate, setBulkEndDate] = useState(""); // End date for bulk assignment

  // Manejar carga de archivo de keywords
  const handleKeywordFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setKeywordFile(file);
    }
  };

  // Manejar carga de archivo de ASINs
  const handleAsinFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setAsinFile(file);
    }
  };

  // Manejar carga de archivo de negative targeting
  const handleNegativeFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setNegativeFile(file);
    }
  };

  // Generate campaigns (generate only, do not create in Amazon yet)
  const handleGenerateCampaigns = async () => {
    if (!keywordFile || !asinFile) {
      setError("You must upload the Keywords and ASINs files");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setCreationDiagnostics(null);
    setGroupingAnalysis(null);

    try {
      const formData = new FormData();

      // Choose endpoint based on mode
      let endpoint;
      if (isGroupingMode && groupingQuery.trim()) {
        // Use AI grouping endpoint
        endpoint = `${API_URL}/keyword-grouping/generate`;
        formData.append("keywords", keywordFile);
        formData.append("asins", asinFile);
        formData.append("groupingQuery", groupingQuery);
      } else {
        // Use regular endpoint
        endpoint = `${API_URL}/spa-campaigns/generate`;
        formData.append("keyword_file", keywordFile);
        formData.append("asin_file", asinFile);
        if (negativeFile) {
          formData.append("negative_file", negativeFile);
        }
      }

      console.log("Calling endpoint:", endpoint);
      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("[SPA UI] Generate response status:", response.status);
      console.log("[SPA UI] Generate response requestId:", response.data?.requestId || "n/a");
      console.log("[SPA UI] Generate response success:", response.data?.success);

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        // Agregar campo 'selected' a cada campaign
        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true, // Por defecto todas selected
        }));

        setCampaigns(campaignsWithSelection);

        let message = `${generatedCampaigns.length} campaigns generated. Select which ones to create in Amazon.`;

        // Add grouping info if available
        if (response.data.grouping_analysis) {
          setGroupingAnalysis({
            analysis: response.data.grouping_analysis,
            summary: response.data.groups_summary,
            total_groups: response.data.total_groups,
          });
          message += ` (${response.data.total_groups} grupos creados)`;
        }

        setResult({
          success: true,
          message: message,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error generating campaigns");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error || err.response?.data?.message || err.message || "Error generating campaigns",
      );
    } finally {
      setLoading(false);
    }
  };

  // Create campaigns selected en Amazon
  const handleCreateInAmazon = async () => {
    const selectedCampaigns = campaigns.filter((c) => c.selected);

    if (selectedCampaigns.length === 0) {
      setError("You must select at least one campaign to create in Amazon");
      return;
    }

    setLoading(true);
    setError(null);
    setCreationDiagnostics(null);

    try {
      // Obtener profileId y countryCode del usuario logueado (desde el selector de cuenta en Header)
      const profileId = localStorage.getItem("selectedAccountValue");
      const countryCode = localStorage.getItem("selectedAccountCountry") || "US";

      if (!profileId) {
        setError(
          "Please select an account in the selector above before creating campaigns",
        );
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("keyword_file", keywordFile); // Re-send files as the backend expects them for creation
      formData.append("asin_file", asinFile);
      formData.append("profileId", profileId);
      formData.append("countryCode", countryCode);
      if (negativeFile) {
        formData.append("negative_file", negativeFile);
      }
      // Send the indices of selected campaigns instead of names (backend regenerates with new UUIDs)
      const selectedIndices = (campaigns || []).map((c, idx) => (c.selected ? idx : -1))
        .filter((idx) => idx !== -1);
      formData.append(
        "selected_campaign_indices",
        JSON.stringify(selectedIndices),
      );

      // CRITICAL FIX: Send the actual modified campaigns so the backend has the bulk actions!
      const campaignsToCreate = campaigns.filter(c => c.selected);
      formData.append("modified_campaigns_data", JSON.stringify(campaignsToCreate));

      formData.append("campaignState", campaignState); // Send selected state ? console.log("[DEBUG Frontend] Sending campaignState : ", campaignState);

      // Llamar al endpoint que crea en Amazon
      console.log("[SPA UI] Calling create endpoint:", `${API_URL}/spa-campaigns/create-in-amazon`);
      console.log("[SPA UI] profileId:", profileId, "selected count:", selectedIndices.length, "campaignState:", campaignState);
      const response = await axios.post(
        `${API_URL}/spa-campaigns/create-in-amazon`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      console.log("[SPA UI] Create response status:", response.status);
      console.log("[SPA UI] Create response requestId:", response.data?.requestId || "n/a");
      if (response.data?.debugTrace) {
        console.log("[SPA UI] Create debugTrace:", response.data.debugTrace);
      }

      const amazonResults = response.data.results || [];
      const creationErrors = response.data.errors || [];
      const debugTrace = response.data.debugTrace || [];
      setCreationDiagnostics({
        requestId: response.data?.requestId || null,
        campaignsCreated: response.data?.campaigns_created || 0,
        campaignsFailed: response.data?.campaigns_failed || 0,
        errors: creationErrors,
        debugTrace,
      });

      if (response.data.success) {
        setResult({
          success: true,
          message: response.data.message,
          count: response.data.campaigns_created,
          amazonResults: amazonResults,
          errors: creationErrors,
        });

        // Solo conservar en la tabla las campañas que fueron seleccionadas para crearse
        const selectedCampaignsToShow = campaigns.filter(c => c.selected);

        // Convertir resultados de Amazon a formato de tabla preservando los campos originales
        const updatedCampaigns = selectedCampaignsToShow.map((campaign) => {
          const successResult = amazonResults.find(r => r.campaignName === campaign['Campaign Name']);
          if (successResult) {
            return {
              ...campaign,
              "Campaign ID": successResult.campaignId,
              "Ad Group ID": successResult.adGroupId,
              "Product Ads": successResult.productAdsCount,
              "Keyword Target": successResult.keywordTargetId ? "Created" : "Pending",
              Status: "Created in Amazon",
              amazonSuccess: true, // If it's in amazonResults, it was successful
              amazonProcessed: true
            };
          }

          const errorResult = creationErrors?.find(r => r.campaignName === campaign['Campaign Name']);
          if (errorResult) {
             return {
               ...campaign,
               Status: "Failed",
               amazonSuccess: false,
               amazonProcessed: true,
               amazonError: errorResult.errors?.[0] || "Unknown error"
             };
          }

          return campaign;
        });

        setCampaigns(updatedCampaigns);
        setCreationComplete(true); // Mark creation as complete
      } else {
        const firstError = creationErrors?.[0]?.errors?.[0];
        const fallback = response.data.error || "Error creating campaigns in Amazon";
        setError(firstError || fallback);
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error ||
          err.message ||
          "Error creating campaigns in Amazon",
      );
    } finally {
      setLoading(false);
    }
  };

  // Toggle selection de una campaign
  const toggleCampaignSelection = (index) => {
    const updatedCampaigns = [...campaigns];
    updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
    setCampaigns(updatedCampaigns);
  };

  // Select todas las campaigns
  const selectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: true }));
    setCampaigns(updatedCampaigns);
  };

  // Deselectr todas las campaigns
  const deselectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: false }));
    setCampaigns(updatedCampaigns);
  };

  // Select campaigns por tipo (Exact, Phrase, Broad)
  const selectCampaignsByType = (type) => {
    const updatedCampaigns = campaigns.map((c) => ({
      ...c,
      selected: c["Match Type"] === type ? true : c.selected,
    }));
    setCampaigns(updatedCampaigns);
  };

  // Apply bulk action to selected campaigns
  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Select a bulk action");
      return;
    }

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Select at least one campaign");
      return;
    }

    const updatedCampaigns = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;

      const updated = { ...campaign };

      switch (bulkAction) {
        case "budget":
          if (bulkBudget && parseFloat(bulkBudget) > 0) {
            updated.Budget = parseFloat(bulkBudget);
          }
          break;
        case "startDate":
          if (bulkStartDate) {
            updated["Start Date"] = bulkStartDate;
          }
          break;
        case "endDate":
          if (bulkEndDate) {
            // Keep both keys to match different payload variants.
            updated["Campaign End Date"] = bulkEndDate;
            updated["End Date"] = bulkEndDate;
          }
          break;
        default:
          break;
      }

      return updated;
    });

    setCampaigns(updatedCampaigns);

    // Reset bulk action inputs
    setBulkAction("");
    setBulkBudget("");
    setBulkStartDate("");
    setBulkEndDate("");

    setError(null);
    setResult({
      success: true,
      message: `Action applied to ${selectedCount} campaign(s)`,
      count: selectedCount,
    });
  };

  // Download campaigns as CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;

    // Convertir a CSV
    const headers = Object.keys(campaigns[0]);
    const csvContent = [
      headers.join(","),
      ...campaigns.map((campaign) =>
        headers
          .map((header) => {
            const value = campaign[header];
            // Escapar valores que contengan comas
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `spa_campaigns_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset
  const handleReset = () => {
    setKeywordFile(null);
    setAsinFile(null);
    setNegativeFile(null);
    setCampaigns([]);
    setResult(null);
    setError(null);
    setCreationDiagnostics(null);
    setCreationComplete(false);
  };

  return (
    <Layout>
      <Container fluid className="spa-keyword-campaigns-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className=" mb-3">
              <FaRocket className="me-2" />
              Sponsored Products - Single Keyword Campaigns
            </h2>
            <p className="text-muted">
              Generate SPA campaigns based on individual keywords with
              ASINs and negative targeting
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {creationDiagnostics && (
          <Alert
            variant={creationDiagnostics.campaignsFailed > 0 ? "warning" : "success"}
            className="mb-4"
          >
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <strong>
                Amazon creation diagnostics
              </strong>
              <small>
                Request ID: {creationDiagnostics.requestId || "n/a"}
              </small>
            </div>
            <div className="mb-2">
              Created: {creationDiagnostics.campaignsCreated} | Failed: {creationDiagnostics.campaignsFailed}
            </div>
            {creationDiagnostics.debugTrace?.length > 0 && (
              <div className="small">
                {creationDiagnostics.debugTrace.map((item, idx) => (
                  <div key={`${item.campaignName || "campaign"}-${idx}`} className="mb-2 p-2 border rounded">
                    <div>
                      <strong>{item.campaignName || `Campaign ${idx + 1}`}</strong>
                      {" "}
                      <Badge bg={item.success ? "success" : "danger"} className="ms-2">
                        {item.success ? "Success" : "Failed"}
                      </Badge>
                    </div>
                    {!item.success && Array.isArray(item.errors) && item.errors.length > 0 && (
                      <ul className="mb-0 mt-1 ps-3">
                        {item.errors.map((detail, detailIdx) => (
                          <li key={`${idx}-${detailIdx}`}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Alert>
        )}

        {/* Upload Section */}
        {!result && (
          <>
            {/* Upload Grid */}
            <div className="upload-grid">
              {/* Card 1: Keywords */}
              <div className={`upload-card-new ${keywordFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Required</div>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Keywords CSV</div>
                <div className="upload-card-desc">
                  Columns: Abbreviation, Keyword. Optional: Bid, Match Type,
                  Budget, Bidding strategy, Start date, End date
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {keywordFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleKeywordFileChange}
                    hidden
                  />
                </label>
                {keywordFile && (
                  <small className="text-success mt-2">
                    {keywordFile.name}
                  </small>
                )}
              </div>

              {/* Card 2: ASINs */}
              <div className={`upload-card-new ${asinFile ? "has-file" : ""}`}>
                <div className="card-required-badge">Required</div>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">ASINs CSV</div>
                <div className="upload-card-desc">
                  Columns: Abbreviation, ASINs. Optional: SKUs
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {asinFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleAsinFileChange}
                    hidden
                  />
                </label>
                {asinFile && (
                  <small className="text-success mt-2">{asinFile.name}</small>
                )}
              </div>

              {/* Card 3: Negative Targeting */}
              <div className={`upload-card-new ${negativeFile ? "has-file" : ""}`}
              >
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">
                  Negative Targeting (Optional)
                </div>
                <div className="upload-card-desc">
                  Columns: Abbreviation, Negative Targeting, Match Type
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {negativeFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleNegativeFileChange}
                    hidden
                  />
                </label>
                {negativeFile && (
                  <small className="text-success mt-2">
                    {negativeFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* AI Grouping Query - Only show in grouping mode */}
            {isGroupingMode && (
              <div className="config-section-card">
                <h5 className="mb-3">
                  <FaBrain className="me-2 text-success" />
                  AI-Powered Grouping
                </h5>
                <div className="text-muted small mb-3">
                  Describe how you want to group the keywords. Examples:
                  <ul className="mt-2 mb-0">
                    <li>"search volume greater than 30k"</li>
                    <li>"search volume between 10k and 50k"</li>
                    <li>"sv above 20k"</li>
                  </ul>
                </div>
                <Form.Group>
                  <Form.Control
                    type="text"
                    className="custom-input" placeholder="Example: search volume greater than 30k"
                    value={groupingQuery}
                    onChange={(e) => setGroupingQuery(e.target.value)}
                  />
                </Form.Group>
                {groupingAnalysis && (
                  <Alert variant="success" className="mt-3 mb-0">
                    <strong>AI Analysis:</strong> {groupingAnalysis.analysis}
                    <br />
                    <strong>Summary:</strong> {groupingAnalysis.summary}
                  </Alert>
                )}
              </div>
            )}

            {/* Generate Button */}
            <div className="generate-section">
              <button className={`btn-generate-campaigns ${!loading && keywordFile && asinFile ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={loading || !keywordFile || !asinFile}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generating campaigns...
                  </>
                ) : (
                  <>
                    <FaRocket />
                    Generate Campaigns
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Results Section */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="  p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaRocket className="me-2" />
                      Generated Campaigns ({campaigns.length})
                    </h5>
                  </div>

                  <div>
                    {/* Bulk Actions Row */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2 align-items-center">
                        <span className=" fw-bold">
                          Bulk actions:
                        </span>
                        <Form.Select
                          size="sm"
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Select action</option>
                          <option value="budget">Set budget...</option>
                          <option value="startDate">Set Start Date...</option>
                          <option value="endDate">Set End Date...</option>
                        </Form.Select>

                        {bulkAction === "budget" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="New budget"
                            value={bulkBudget}
                            onChange={(e) => setBulkBudget(e.target.value)}
                            style={{ width: "150px" }}
                            min="0"
                            step="0.01"
                          />
                        )}

                        {bulkAction === "startDate" && (
                          <Form.Control
                            type="date"
                            size="sm"
                            value={bulkStartDate}
                            onChange={(e) => setBulkStartDate(e.target.value)}
                            style={{ width: "150px" }}
                          />
                        )}

                        {bulkAction === "endDate" && (
                          <Form.Control
                            type="date"
                            size="sm"
                            value={bulkEndDate}
                            onChange={(e) => setBulkEndDate(e.target.value)}
                            style={{ width: "150px" }}
                          />
                        )}

                        {bulkAction && (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={applyBulkAction}
                          >
                            Apply
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      {/* Main Actions */}
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={campaignState}
                          onChange={(e) => setCampaignState(e.target.value)}
                          style={{ width: "auto" }}
                          disabled={creationComplete}
                        >
                          <option value="ENABLED">Create as: Enabled</option>
                          <option value="PAUSED">Create as: Paused</option>
                        </Form.Select>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleDownloadCampaigns}
                        >
                          <FaDownload className="me-1" /> Download CSV
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleReset}
                        >
                          <FaRocket className="me-1" /> New Generation
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive " style={{ maxHeight : "600px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    
                    className="align-middle mb-0"
                    size="sm" style={{ fontSize : "0.85rem" }}
                  >
                    <thead className="text-warning sticky-top ">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th width="50">#</th>
                        <th width="350">Campaign Name</th>
                        <th>Keyword</th>
                        <th width="120">Match Type</th>
                        <th width="70">Bid</th>
                        <th width="80">Budget</th>
                        <th width="120">Start Date</th>
                        <th width="120">End Date</th>
                        <th width="100">ASINs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={idx} style={campaign.amazonSuccess ? { backgroundColor: 'rgba(40, 167, 69, 0.15)' } : {}}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={campaign.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                              disabled={campaign.amazonProcessed}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              style={{ wordBreak: "break-all", whiteSpace: "normal", fontWeight: campaign.amazonSuccess ? "bold" : "normal" }}
                            >
                              {campaign["Campaign Name"]}
                              {campaign.amazonSuccess && (
                                <Badge bg="success" className="ms-2">Created</Badge>
                              )}
                              {campaign.amazonProcessed && !campaign.amazonSuccess && (
                                <Badge bg="danger" className="ms-2">Failed</Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-info">{campaign.Keyword}</td>
                          <td>
                            <Badge
                              bg={
                                campaign["Match Type"] === "Exact" ? "success" : campaign["Match Type"] === "Phrase" ? "warning" : "secondary"
                              }
                            >
                              {campaign["Match Type"]}
                            </Badge>
                          </td>
                          <td>${campaign.Bid}</td>
                          <td>${campaign.Budget}</td>
                          <td>{campaign["Start Date"] ? new Date(campaign["Start Date"]).toLocaleString() : "-"}</td>
                          <td>{(campaign["Campaign End Date"] || campaign["End Date"]) ? new Date(campaign["Campaign End Date"] || campaign["End Date"]).toLocaleString() : "-"}</td>
                          <td>
                            <div
                              className="text-truncate" style={{ maxWidth : "80px" }}
                              title={campaign.ASINs}
                            >
                              {campaign.ASINs ? campaign.ASINs.split(",").length : 0}{" "}
                              ASINs
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className=" border-top border-secondary d-flex justify-content-between p-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="fw-bold"
                    >
                      ← Cancel
                    </Button>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="text-muted small">
                      Total: {campaigns.length}
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                      style={{
                        fontWeight: "bold",
                        padding: "6px 20px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Creating in Amazon...
                        </>
                      ) : creationComplete ? (
                        "Campaigns Created" ) : (
                        "Create Selected Campaigns"
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements Section */}
        {!result && (
          <div className="requirements-section">
            <span className="requirements-title">
              📋 CSV File Requirements
            </span>
            <div className="req-item">
              <strong>Keywords CSV:</strong> Must include columns 'Abbreviation' and 'Keyword'. Optional: Bid, Match Type, Budget,
              Bidding strategy, Start date, End date.
            </div>
            <div className="req-item">
              <strong>ASINs CSV:</strong> Must include columns 'Abbreviation' and
              'ASINs'. Optional: SKUs.
            </div>
            <div className="req-item">
              <strong>Negative Targeting CSV:</strong> Must include columns 'Abbreviation', 'Negative Targeting', and 'Match Type'.
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpaSingleKeyword;



